import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Trash2, Library, BookOpen, X, Upload, FileText, Share2, Copy, Link as LinkIcon, Globe, ExternalLink, Sparkles, Loader2, Presentation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import PresentationView from "@/components/PresentationView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomStudiesPage() {
  const [searchParams] = useSearchParams();
  const [studies, setStudies] = useState([]);
  const [sharedStudies, setSharedStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [shareLink, setShareLink] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bibleSearchOpen, setBibleSearchOpen] = useState(false);
  const [bibleQuery, setBibleQuery] = useState("");
  const [bibleResults, setBibleResults] = useState([]);
  const [searchingBible, setSearchingBible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [currentTab, setCurrentTab] = useState("my-studies");
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [customizationResult, setCustomizationResult] = useState(null);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("teen");
  const [presentingStudy, setPresentingStudy] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    topic: "",
    category: "",
    scriptures: [],
    notes: ""
  });

  useEffect(() => {
    fetchStudies();
    
    // Check if there's a share token in URL
    const shareToken = searchParams.get('share');
    if (shareToken) {
      fetchSharedStudy(shareToken);
    }
  }, [searchParams]);

  const fetchStudies = async (search = "") => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/custom-studies`, { 
        params,
        withCredentials: true 
      });
      setStudies(response.data);
    } catch (error) {
      console.error("Failed to fetch custom studies", error);
      toast.error("Failed to load custom studies");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchStudies(query);
  };

  const searchBible = async () => {
    if (!bibleQuery.trim()) return;
    
    try {
      setSearchingBible(true);
      const response = await axios.get(`${API}/bible/search`, {
        params: { query: bibleQuery },
        withCredentials: true
      });
      
      if (response.data.data && response.data.data.verses && response.data.data.verses.length > 0) {
        setBibleResults(response.data.data.verses);
        toast.success(`Found ${response.data.data.verses.length} verse(s)`);
      } else {
        setBibleResults([]);
        toast.info("No results found");
      }
    } catch (error) {
      console.error("Bible search failed", error);
      // Extract error message from backend
      const errorMessage = error.response?.data?.detail || "Bible search failed. Please try again.";
      toast.error(errorMessage, { duration: 5000 });
      setBibleResults([]);
    } finally {
      setSearchingBible(false);
    }
  };

  const addScripture = (verse) => {
    const scripture = {
      reference: verse.reference,
      text: verse.text
    };
    
    setFormData({
      ...formData,
      scriptures: [...formData.scriptures, scripture]
    });
    
    toast.success("Scripture added");
  };

  const removeScripture = (index) => {
    const newScriptures = formData.scriptures.filter((_, i) => i !== index);
    setFormData({ ...formData, scriptures: newScriptures });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/custom-studies`, formData, { withCredentials: true });
      toast.success("Custom study created successfully");
      setDialogOpen(false);
      setFormData({
        title: "",
        topic: "",
        category: "",
        scriptures: [],
        notes: ""
      });
      fetchStudies();
    } catch (error) {
      console.error("Failed to create custom study", error);
      toast.error("Failed to create custom study");
    }
  };

  const deleteStudy = async (studyId) => {
    if (!window.confirm("Are you sure you want to delete this custom study?")) return;
    
    try {
      await axios.delete(`${API}/custom-studies/${studyId}`, { withCredentials: true });
      toast.success("Custom study deleted");
      fetchStudies();
    } catch (error) {
      console.error("Failed to delete custom study", error);
      toast.error("Failed to delete custom study");
    }
  };

  const handleShare = async (study) => {
    setSelectedStudy(study);
    setIsPublic(study.is_public || false);
    
    if (study.share_token) {
      setShareLink(`${window.location.origin}/custom-studies?share=${study.share_token}`);
    }
    
    setShareDialogOpen(true);
  };

  const generateShareLink = async () => {
    try {
      const response = await axios.post(
        `${API}/custom-studies/${selectedStudy.id}/share?make_public=${isPublic}`,
        {},
        { withCredentials: true }
      );
      
      setShareLink(response.data.share_link);
      toast.success(isPublic ? "Study is now public!" : "Share link generated!");
      fetchStudies();
    } catch (error) {
      console.error("Failed to generate share link", error);
      toast.error("Failed to share study");
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Link copied to clipboard!");
  };

  const unshareStudy = async (studyId) => {
    try {
      await axios.delete(`${API}/custom-studies/${studyId}/unshare`, { withCredentials: true });
      toast.success("Study unshared");
      fetchStudies();
    } catch (error) {
      console.error("Failed to unshare", error);
      toast.error("Failed to unshare study");
    }
  };

  const fetchSharedStudies = async () => {
    try {
      setSharedLoading(true);
      const response = await axios.get(`${API}/custom-studies/shared/public`, { 
        withCredentials: true 
      });
      setSharedStudies(response.data);
    } catch (error) {
      console.error("Failed to fetch shared studies", error);
      toast.error("Failed to load shared studies");
    } finally {
      setSharedLoading(false);
    }
  };

  const fetchSharedStudy = async (shareToken) => {
    try {
      const response = await axios.get(`${API}/custom-studies/shared/${shareToken}`, { 
        withCredentials: true 
      });
      
      // Show the shared study in a toast with option to copy
      toast.info(`Viewing shared study: ${response.data.title}`, {
        description: "Switch to 'Shared Library' tab to copy this study",
        duration: 5000
      });
      
      setCurrentTab("shared");
      fetchSharedStudies();
    } catch (error) {
      console.error("Failed to fetch shared study", error);
      toast.error("Invalid share link");
    }
  };

  const copySharedStudy = async (shareToken) => {
    try {
      await axios.post(`${API}/custom-studies/shared/${shareToken}/copy`, {}, { 
        withCredentials: true 
      });
      toast.success("Study copied to your library!");
      fetchStudies();
    } catch (error) {
      console.error("Failed to copy study", error);
      toast.error("Failed to copy study");
    }
  };

  const handleCustomize = async (study) => {
    setSelectedStudy(study);
    setCustomizeDialogOpen(true);
    setCustomizationResult(null);
  };

  const generateCustomization = async () => {
    if (!selectedStudy) return;

    try {
      setCustomizing(true);
      const response = await axios.post(
        `${API}/custom-studies/${selectedStudy.id}/customize?age_group=${selectedAgeGroup}`,
        {},
        { withCredentials: true }
      );
      
      setCustomizationResult(response.data.customizations);
      toast.success("Customization suggestions generated!");
    } catch (error) {
      console.error("Failed to customize study", error);
      toast.error(error.response?.data?.detail || "Failed to generate suggestions");
    } finally {
      setCustomizing(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImporting(true);
      const response = await axios.post(`${API}/custom-studies/import`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Successfully imported ${response.data.imported_count} studies!`);
      setImportDialogOpen(false);
      fetchStudies();
    } catch (error) {
      console.error("Import failed", error);
      toast.error(error.response?.data?.detail || "Failed to import studies");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      {presentingStudy && (
        <PresentationView
          study={presentingStudy}
          onClose={() => setPresentingStudy(null)}
        />
      )}
      <PageLayout title="Custom Bible Studies">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-custom-studies-input"
            type="text"
            placeholder="Search custom studies..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="import-study-button" variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Import Bible Studies</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Import studies from JSON, CSV, or TXT files
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.csv,.txt"
                    onChange={handleImport}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                      {importing ? "Importing..." : "Click to upload file"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supports JSON, CSV, and TXT formats
                    </p>
                  </label>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg text-xs space-y-3">
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Format Examples:</p>
                    <div className="space-y-1">
                      <p className="text-gray-600"><strong>JSON:</strong> [{`{"title":"...", "topic":"...", "scriptures":[...]}`}]</p>
                      <p className="text-gray-600"><strong>CSV:</strong> title,topic,category,scriptures,notes</p>
                      <p className="text-gray-600"><strong>TXT:</strong> Title: ..., Topic: ..., Scripture: ...</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-semibold text-gray-700 mb-2">Download Sample Templates:</p>
                    <div className="flex gap-2 flex-wrap">
                      <a 
                        href="/templates/sample_import_template.json" 
                        download 
                        className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        JSON
                      </a>
                      <a 
                        href="/templates/sample_import_template.csv" 
                        download 
                        className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        CSV
                      </a>
                      <a 
                        href="/templates/sample_import_template.txt" 
                        download 
                        className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        TXT
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-study-button" className="bg-emerald-700 hover:bg-emerald-800">
                <Plus className="w-4 h-4 mr-2" />
                Create Study
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Custom Bible Study</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <Input
                  data-testid="study-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="e.g., God's Love and Grace"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
                  <Input
                    data-testid="study-topic-input"
                    value={formData.topic}
                    onChange={(e) => setFormData({...formData, topic: e.target.value})}
                    required
                    placeholder="e.g., Love"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <Input
                    data-testid="study-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g., Doctrine"
                  />
                </div>
              </div>

              {/* Bible Search Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Scriptures (NKJV)</label>
                  <Button 
                    data-testid="toggle-bible-search-button"
                    type="button" 
                    size="sm" 
                    variant="outline"
                    onClick={() => setBibleSearchOpen(!bibleSearchOpen)}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    {bibleSearchOpen ? 'Close' : 'Search Bible'}
                  </Button>
                </div>

                {bibleSearchOpen && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-xs text-gray-600 mb-2">
                      💡 <strong>Tip:</strong> Enter a specific verse reference (e.g., "John 3:16", "Romans 8:28-30", "Psalm 23")
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Input
                        data-testid="bible-search-input"
                        value={bibleQuery}
                        onChange={(e) => setBibleQuery(e.target.value)}
                        placeholder="e.g., John 3:16, Romans 8:28-30"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchBible())}
                      />
                      <Button 
                        data-testid="bible-search-button"
                        type="button" 
                        onClick={searchBible}
                        disabled={searchingBible}
                      >
                        {searchingBible ? 'Searching...' : 'Search'}
                      </Button>
                    </div>
                    
                    {bibleResults.length > 0 && (
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {bibleResults.map((verse, idx) => (
                          <div key={idx} className="bg-white p-3 rounded border">
                            <div className="flex justify-between items-start gap-2">
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-emerald-700 mb-1">{verse.reference}</p>
                                <p className="text-sm text-gray-700">{verse.text}</p>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => addScripture(verse)}
                              >
                                Add
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Scriptures */}
                {formData.scriptures.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Selected Scriptures:</p>
                    {formData.scriptures.map((scripture, idx) => (
                      <div key={idx} className="bg-emerald-50 p-3 rounded-lg flex justify-between items-start gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-emerald-800">{scripture.reference}</p>
                          <p className="text-sm text-gray-700 mt-1">{scripture.text}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeScripture(idx)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Study Notes</label>
                <Textarea
                  data-testid="study-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Add your study notes, key points, applications..."
                  rows={5}
                />
              </div>
              <Button data-testid="submit-study-button" type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                Create Custom Study
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Bible Study</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium">Make Public</span>
              </div>
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
            </div>
            <p className="text-xs text-gray-500">
              {isPublic ? "Anyone can discover this study in the shared library" : "Only people with the link can view"}
            </p>

            <Button 
              onClick={generateShareLink} 
              className="w-full"
              data-testid="generate-share-link"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {shareLink ? "Update Settings" : "Generate Share Link"}
            </Button>

            {shareLink && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Share Link</label>
                <div className="flex gap-2">
                  <Input value={shareLink} readOnly className="text-sm" />
                  <Button onClick={copyShareLink} variant="outline" size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize Dialog */}
      <Dialog open={customizeDialogOpen} onOpenChange={setCustomizeDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customize Study for Age Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Get AI-powered suggestions to adapt this study for teens or college students
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setSelectedAgeGroup("teen")}
                  variant={selectedAgeGroup === "teen" ? "default" : "outline"}
                  className={selectedAgeGroup === "teen" ? "bg-emerald-700" : ""}
                >
                  Teens (13-18)
                </Button>
                <Button
                  onClick={() => setSelectedAgeGroup("college")}
                  variant={selectedAgeGroup === "college" ? "default" : "outline"}
                  className={selectedAgeGroup === "college" ? "bg-emerald-700" : ""}
                >
                  College (18-25)
                </Button>
              </div>
            </div>

            <Button
              onClick={generateCustomization}
              disabled={customizing}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="generate-customization-button"
            >
              {customizing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Suggestions...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Customization
                </>
              )}
            </Button>

            {customizationResult && (
              <div className="space-y-4 mt-6 border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900">Suggestions</h3>
                
                {customizationResult.simplified_language && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Simplified Language</h4>
                    <p className="text-sm text-gray-700 mb-3">{customizationResult.simplified_language.explanation}</p>
                    {customizationResult.simplified_language.modern_examples && (
                      <div>
                        <p className="text-sm font-medium text-blue-800 mb-1">Modern Examples:</p>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                          {customizationResult.simplified_language.modern_examples.map((ex, idx) => (
                            <li key={idx}>{ex}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {customizationResult.discussion_questions && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">Discussion Questions</h4>
                    <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2">
                      {customizationResult.discussion_questions.map((q, idx) => (
                        <li key={idx}>{q}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {customizationResult.activities && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-900 mb-2">Activities</h4>
                    <div className="space-y-3">
                      {customizationResult.activities.map((activity, idx) => (
                        <div key={idx} className="bg-white p-3 rounded border border-purple-200">
                          <p className="font-medium text-purple-900">{activity.name}</p>
                          <p className="text-sm text-gray-700 mt-1">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-1">Duration: {activity.duration}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {customizationResult.teaching_tips && (
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-amber-900 mb-2">Teaching Tips</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      {customizationResult.teaching_tips.map((tip, idx) => (
                        <li key={idx}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="my-studies">My Studies</TabsTrigger>
          <TabsTrigger value="shared" onClick={() => fetchSharedStudies()}>Shared Library</TabsTrigger>
        </TabsList>

        <TabsContent value="my-studies">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            </div>
          ) : studies.length === 0 ? (
            <div className="text-center py-12">
              <Library className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No custom studies created yet</p>
              <p className="text-sm text-gray-400 mt-1">Start creating personalized Bible studies</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {studies.map(study => (
                <CustomStudyCard 
                  key={study.id}
                  study={study}
                  onDelete={() => deleteStudy(study.id)}
                  onShare={() => handleShare(study)}
                  onUnshare={() => unshareStudy(study.id)}
                  onCustomize={() => handleCustomize(study)}
                  onPresent={() => setPresentingStudy(study)}
                  isOwner={true}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="shared">
          {sharedLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            </div>
          ) : sharedStudies.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No shared studies available yet</p>
              <p className="text-sm text-gray-400 mt-1">Be the first to share a study!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {sharedStudies.map(study => (
                <CustomStudyCard 
                  key={study.id}
                  study={study}
                  onCopy={() => copySharedStudy(study.share_token)}
                  onPresent={() => setPresentingStudy(study)}
                  isOwner={false}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageLayout>
    </>
  );
}

function CustomStudyCard({ study, onDelete, onShare, onUnshare, onCopy, onCustomize, onPresent, isOwner }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{study.title}</h3>
            {study.category && (
              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                {study.category}
              </span>
            )}
            {study.is_public && (
              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                <Globe className="w-3 h-3" />
                Public
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm text-gray-600">Topic: {study.topic}</p>
            {study.author_name && !isOwner && (
              <span className="text-xs text-gray-500">by {study.author_name}</span>
            )}
          </div>
          
          {study.scriptures && study.scriptures.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">Scriptures:</p>
              <div className="space-y-2">
                {study.scriptures.map((scripture, idx) => (
                  <div key={idx} className="bg-emerald-50 p-2 rounded text-xs">
                    <p className="font-semibold text-emerald-800">{scripture.reference}</p>
                    <p className="text-gray-700 mt-1">{scripture.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {study.notes && (
            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{study.notes}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Button
            data-testid={`custom-study-present-${study.id}`}
            onClick={onPresent}
            variant="ghost"
            size="sm"
            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
            title="Present study"
          >
            <Presentation className="w-4 h-4" />
          </Button>
          {isOwner ? (
            <>
              {study.share_token ? (
                <Button
                  data-testid={`custom-study-unshare-${study.id}`}
                  onClick={onUnshare}
                  variant="ghost"
                  size="sm"
                  className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                  title="Stop sharing"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  data-testid={`custom-study-share-${study.id}`}
                  onClick={onShare}
                  variant="ghost"
                  size="sm"
                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                  title="Share study"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              )}
              <Button
                data-testid={`custom-study-customize-${study.id}`}
                onClick={onCustomize}
                variant="ghost"
                size="sm"
                className="text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                title="Customize for age group"
              >
                <Sparkles className="w-4 h-4" />
              </Button>
              <Button
                data-testid={`custom-study-delete-${study.id}`}
                onClick={onDelete}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                title="Delete study"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button
              data-testid={`custom-study-copy-${study.id}`}
              onClick={onCopy}
              variant="ghost"
              size="sm"
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              title="Copy to my library"
            >
              <Copy className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
